import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ProductPrice } from '@/schemas/prices';

interface PricesState {
  byId: Record<string, ProductPrice>;
  allIds: string[];
  byVariantId: Record<string, string[]>;
  filters: {
    variantId?: string;
    priceType?: string;
    isActive?: boolean;
  };
  selectedPriceId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: PricesState = {
  byId: {},
  allIds: [],
  byVariantId: {},
  filters: {},
  selectedPriceId: null,
  isLoading: false,
  error: null,
};

const pricesSlice = createSlice({
  name: 'prices',
  initialState,
  reducers: {
    setPrices: (state, action: PayloadAction<ProductPrice[]>) => {
      state.byId = {};
      state.allIds = [];
      state.byVariantId = {};
      action.payload.forEach((price) => {
        state.byId[price.id] = price;
        state.allIds.push(price.id);

        // Build variant index
        if (!state.byVariantId[price.variant_id]) {
          state.byVariantId[price.variant_id] = [];
        }
        state.byVariantId[price.variant_id].push(price.id);
      });
    },
    addPrice: (state, action: PayloadAction<ProductPrice>) => {
      const price = action.payload;
      state.byId[price.id] = price;
      if (!state.allIds.includes(price.id)) {
        state.allIds.push(price.id);
      }

      // Update variant index
      if (!state.byVariantId[price.variant_id]) {
        state.byVariantId[price.variant_id] = [];
      }
      if (!state.byVariantId[price.variant_id].includes(price.id)) {
        state.byVariantId[price.variant_id].push(price.id);
      }
    },
    updatePrice: (state, action: PayloadAction<ProductPrice>) => {
      const price = action.payload;
      state.byId[price.id] = price;
    },
    deletePrice: (state, action: PayloadAction<string>) => {
      const price = state.byId[action.payload];
      if (price) {
        // Remove from variant index
        state.byVariantId[price.variant_id] = state.byVariantId[price.variant_id].filter(
          (id) => id !== action.payload
        );
      }
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setSelectedPriceId: (state, action: PayloadAction<string | null>) => {
      state.selectedPriceId = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<PricesState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setPrices,
  addPrice,
  updatePrice,
  deletePrice,
  setSelectedPriceId,
  setFilters,
  setLoading,
  setError,
} = pricesSlice.actions;

export default pricesSlice.reducer;
